import { CheckCircle2, XCircle, Clock, AlertTriangle, GitCompare } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface BulkImportSummaryProps {
  result: {
    success: number;
    failed: number;
    duplicates: number;
    errors: any[];
    processingTimeMs?: number;
  };
  // onClose removed - overlay footer handles closure (Overlay Contract)
}

export function BulkImportSummary({ result }: BulkImportSummaryProps) {
  const { t } = useLanguage();
  
  // Actual processing time (what the system took)
  const actualProcessingSeconds = result.processingTimeMs 
    ? Math.ceil(result.processingTimeMs / 1000) 
    : 0;

  // Estimated manual time (only for successfully imported orders)
  const MANUAL_MINUTES_PER_ORDER = 2; // Conservative estimate: 2 minutes per order
  const estimatedManualMinutes = result.success * MANUAL_MINUTES_PER_ORDER;

  // Time saved = manual time - actual time
  const timeSavedMinutes = Math.max(0, estimatedManualMinutes - Math.ceil(actualProcessingSeconds / 60));

  return (
    <div className="space-y-6 py-6">
      <div className="text-center space-y-4">
        {result.success > 0 && (
          <div className="flex items-center justify-center gap-3 text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-12 w-12" />
            <div className="text-left">
              <h3 className="text-2xl font-bold">{t("bulk.summary.title")}</h3>
              <p className="text-lg">
                {t(result.success === 1 ? "bulk.summary.success" : "bulk.summary.successPlural").replace('{{count}}', String(result.success))}
              </p>
            </div>
          </div>
        )}

        {result.success === 0 && (
          <div className="flex items-center justify-center gap-3 text-amber-600 dark:text-amber-400">
            <XCircle className="h-12 w-12" />
            <div className="text-left">
              <h3 className="text-2xl font-bold">{t("bulk.summary.failedToImport")}</h3>
              <p className="text-lg">{t("bulk.rejected.empty")}</p>
            </div>
          </div>
        )}
      </div>

      {/* Detailed breakdown - stacked single-column (Sheet Contract) */}
      <div className="space-y-3">
        <div className="border rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <span className="text-sm text-muted-foreground">{t("bulk.summary.imported")}</span>
          </div>
          <span className="text-2xl font-bold text-green-600 dark:text-green-400">{result.success}</span>
        </div>

        <div className="border rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <span className="text-sm text-muted-foreground">{t("bulk.summary.failedToImport")}</span>
          </div>
          <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">{result.failed}</span>
        </div>

        <div className="border rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitCompare className="h-5 w-5 text-orange-500" />
            <span className="text-sm text-muted-foreground">{t("bulk.summary.duplicatesFound")}</span>
          </div>
          <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">{result.duplicates}</span>
        </div>
      </div>

      {result.success > 0 && result.processingTimeMs && (
        <div className="bg-muted/50 rounded-lg p-6 space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              {t("bulk.summary.processedIn")} <strong className="text-foreground">{actualProcessingSeconds} {t("bulk.summary.seconds")}</strong>
            </span>
          </div>
          <div className="text-sm text-muted-foreground">
            <span>
              {t("bulk.summary.estimatedManualEntry")} <strong className="text-foreground">~{estimatedManualMinutes} {t("bulk.summary.minutes")}</strong>
            </span>
          </div>
          <div className="text-base text-green-600 dark:text-green-400 font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t("bulk.summary.timeSaved")} <strong>{timeSavedMinutes} {t("bulk.summary.minutes")}</strong>
          </div>
        </div>
      )}

      {result.errors.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="font-medium text-red-900 dark:text-red-100 mb-2">
            {t("bulk.summary.errorDetails")}
          </p>
          <div className="space-y-1 text-sm text-red-700 dark:text-red-300 max-h-40 overflow-y-auto">
            {result.errors.slice(0, 10).map((err, idx) => (
              <div key={idx}>
                • Order {err.order || 'N/A'}: {err.error}
              </div>
            ))}
            {result.errors.length > 10 && (
              <div className="text-xs">... and {result.errors.length - 10} more</div>
            )}
          </div>
        </div>
      )}
      {/* Close button removed - overlay footer handles closure (Overlay Contract) */}
    </div>
  );
}